import {
  faBell,
  faRightFromBracket,
  faRightToBracket,
  faUser,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Button, Dropdown, Layout } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import colors from '../../../foundation/assets/colors';
import LogoDark from '../../../foundation/assets/svgs/LogoDark';
import { clearStorage } from '../../../foundation/utils/storageHandler';
import { useAppDispatch } from '../../../store/hooks';
import { apiSlice } from '../../api/apiSlice';
import {
  useGetVariantDetailQuery,
  useReleaseVariantLockMutation,
} from '../../api/variantApi';
import Login from '../../authentication/login/Login';
import UserProfileModal from '../../authentication/profile/UserProfileModal';
import { logout } from '../../authentication/redux/async_thunks';
import {
  selectUser,
  selectUsername,
} from '../../authentication/redux/selectors';
import { setUsername } from '../../authentication/redux/slice';
import ImprovedSignupModal from '../../authentication/signup/ImprovedSignupModal';
import { selectVariantLock } from '../../variant_workflow/redux';

const { Header: LayoutHeader } = Layout;

type HeaderProps = {
  hideUserProfile?: boolean;
};

const Header = ({ hideUserProfile = false }: HeaderProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isLoginModalVisible, setLoginModalVisibility] = useState(false);
  const [isSignupModalVisible, setSignupModalVisibility] = useState(false);
  const [isProfileModalVisible, setProfileModalVisibility] = useState(false);

  const user = useSelector(selectUser);
  const username = useSelector(selectUsername);
  const lock = useSelector(selectVariantLock);
  // Header is rendered outside the variant-workflow route subtree, so
  // useParams() would not see :vid here - useMatch reads the URL directly.
  const workflowMatch = useMatch('/variant-workflow/:vid/*');
  const vid = workflowMatch?.params.vid;
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader / page components) rather than a Redux selector.
  const { data: selectedVariant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Mutation hook returns a [trigger, result] tuple, replacing the old
  // dispatch(releaseVariantLock(...)).unwrap() pattern
  const [releaseVariantLockMutation] = useReleaseVariantLockMutation();

  const handleLogout = async () => {
    // Release the variant lock before the access token is cleared.
    if (lock?.locked_by === user?.username && selectedVariant?.id) {
      try {
        await releaseVariantLockMutation({
          variant_id: selectedVariant.id,
        }).unwrap();
      } catch {
        // ignore – proceed with logout regardless
      }
    }
    try {
      // Call backend to clear the HttpOnly refresh cookie
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.log('Logout API error:', error);
    } finally {
      // Always clear local state, even if API call fails.
      navigate('/', { replace: true });
      // Clear the minimal auth marker from the Redux slice.
      dispatch(setUsername(undefined));
      // Reset the entire RTK Query cache so no stale user profile or variant
      // data persists for the next session.
      dispatch(apiSlice.util.resetApiState());
      clearStorage();
    }
  };

  const handleLoginClick = () => {
    setLoginModalVisibility(true);
  };

  const handleSignupClick = () => {
    setSignupModalVisibility(true);
  };

  const onLoginModalClose = () => {
    setLoginModalVisibility(false);
  };

  const onSignupModalClose = () => {
    setSignupModalVisibility(false);
  };

  const welcomeTitle = useMemo(
    () => (
      <div className="layout__header-menu-title">
        Welcome
        {user ? ` ${user.name}` : '!'}
      </div>
    ),
    [user],
  );

  const menu: ItemType[] = useMemo(
    () => [
      {
        key: '1',
        label: welcomeTitle,
        disabled: true,
      },
      {
        type: 'divider',
        key: 'divider-1',
      },
      {
        key: '2',
        label: 'Profile',
        icon: <FontAwesomeIcon icon={faUser} size="lg" />,
        onClick: () => setProfileModalVisibility(true),
      },
      {
        key: '3',
        label: 'Balances',
        icon: <FontAwesomeIcon icon={faWallet} size="lg" />,
      },
      {
        key: '4',
        label: 'Alerts',
        icon: <FontAwesomeIcon icon={faBell} size="lg" />,
      },
      {
        type: 'divider',
        key: 'divider-2',
      },
      {
        key: '5',
        icon: <FontAwesomeIcon icon={faRightFromBracket} size="lg" />,
        onClick: handleLogout,
        label: `Logout`,
      },
    ],
    [welcomeTitle],
  );

  return (
    <>
      {isLoginModalVisible && <Login onClose={onLoginModalClose} />}
      {isSignupModalVisible && (
        <ImprovedSignupModal onClose={onSignupModalClose} />
      )}
      {isProfileModalVisible && (
        <UserProfileModal onClose={() => setProfileModalVisibility(false)} />
      )}
      <LayoutHeader className="layout__header">
        <div className="layout__header-logo" onClick={() => navigate('/')}>
          <LogoDark />
        </div>
        {/* Auth guard uses selectUsername (sync Redux slice) instead of selectUser
            (async RTK Query cache) to avoid flashing login buttons while the cache
            is still populating after a page refresh. */}
        {!hideUserProfile && username && (
          <div>
            <Dropdown
              menu={{ items: menu }}
              overlayClassName="layout__header-menu"
            >
              <div className="layout__header-user">
                <div style={{ marginRight: 5 }}>
                  <Avatar
                    icon={<FontAwesomeIcon icon={faUser} size="xs" />}
                    size={'large'}
                    className="layout__header-user-avatar"
                  />
                </div>
              </div>
            </Dropdown>
          </div>
        )}
        {!hideUserProfile && !username && (
          <div>
            <Button
              className="l-landing__header-btn"
              shape="round"
              type="primary"
              icon={<FontAwesomeIcon icon={faUser} />}
              onClick={handleLoginClick}
            >
              Login
            </Button>
            <Button
              className="l-landing__header-btn"
              variant="outlined"
              shape="round"
              onClick={handleSignupClick}
              icon={
                <FontAwesomeIcon
                  icon={faRightToBracket}
                  color={colors.primary}
                />
              }
            >
              Signup
            </Button>
          </div>
        )}
      </LayoutHeader>
    </>
  );
};

export default Header;
